import { useEffect, useMemo, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/ui-extra";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Upload, CheckCircle2, Loader2, Download, FileType, Banknote, Clock, Sparkles, CircleDollarSign, ReceiptText, Eye, FileDown, Pencil, Calendar as CalendarIcon, Search, X } from "lucide-react";
import { toast } from "sonner";
import { safeFormatDate, safeFormatDateOnly, toDate } from "@/lib/dates";
// pdfReport is dynamically imported at call time to keep jsPDF off the initial bundle.
import { formatCurrency } from "@/lib/currency";

// ─── Date-range presets ────────────────────────────────────────────────
// Every value is a function returning `{start, end}` ISO date strings
// (YYYY-MM-DD) or `null` values for open-ended ranges. `all` sends no
// range at all so the endpoint returns the newest 500 unconditionally.
const RANGE_PRESETS = [
  { key: "7d",       label: "Last 7 days",   compute: () => rangeDaysAgo(7) },
  { key: "30d",      label: "Last 30 days",  compute: () => rangeDaysAgo(30) },
  { key: "month",    label: "This month",    compute: () => rangeThisMonth() },
  { key: "last_month", label: "Last month",  compute: () => rangeLastMonth() },
  { key: "ytd",      label: "Year to date",  compute: () => rangeYTD() },
  { key: "all",      label: "All time",      compute: () => ({ start: null, end: null }) },
  { key: "custom",   label: "Custom…",       compute: null },
];
const DEFAULT_PRESET = "30d";
const PRESET_STORAGE_KEY = "invoices.rangePreset.v1";
const CUSTOM_STORAGE_KEY = "invoices.customRange.v1";

function toISO(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dy}`;
}
function rangeDaysAgo(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { start: toISO(start), end: toISO(end) };
}
function rangeThisMonth() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toISO(start), end: toISO(end) };
}
function rangeLastMonth() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { start: toISO(start), end: toISO(end) };
}
function rangeYTD() {
  const now = new Date();
  return { start: toISO(new Date(now.getFullYear(), 0, 1)), end: toISO(now) };
}

export default function InvoicesPage() {
  const { isAdmin, user } = useAuth();
  const isMaster = !!user?.is_master;
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false); // lazy detail load
  const [acting, setActing] = useState(null);
  const [editing, setEditing] = useState(null); // master-admin invoice editor
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // View filter — driven by the clickable stat cards. Persisted in the URL
  // so a Dashboard KPI link like `/invoices?view=outstanding` lands users
  // on the filtered view directly.
  //   all         → show every invoice inside the date range (default)
  //   outstanding → unpaid only
  //   paid        → paid only
  //   thisMonth   → invoices with invoice_date in the current calendar month
  const VALID_VIEWS = ["all", "outstanding", "paid", "thisMonth"];
  const initialView = VALID_VIEWS.includes(searchParams.get("view"))
    ? searchParams.get("view")
    : "all";
  const [view, setView] = useState(initialView);
  // Free-text search across supplier / invoice number / category / notes.
  // Applied on top of the date-range + view filter so users can drill in
  // to a specific supplier or reference without losing their range.
  const [search, setSearch] = useState("");
  const setViewAndUrl = (next) => {
    setView(next);
    // Keep the URL in sync so users can bookmark / share a filtered view.
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "all") params.delete("view");
        else params.set("view", next);
        return params;
      },
      { replace: true },
    );
  };

  // Range picker state — persisted per device so the user's last choice
  // is remembered next visit.
  const [rangeKey, setRangeKey] = useState(() => {
    try { return localStorage.getItem(PRESET_STORAGE_KEY) || DEFAULT_PRESET; }
    catch { return DEFAULT_PRESET; }
  });
  const [customRange, setCustomRange] = useState(() => {
    try {
      const raw = localStorage.getItem(CUSTOM_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { start: toISO(new Date(new Date().setDate(new Date().getDate() - 30))), end: toISO(new Date()) };
  });

  // Compute the active {start,end} based on preset choice.
  const activeRange = useMemo(() => {
    const preset = RANGE_PRESETS.find((p) => p.key === rangeKey);
    if (!preset) return { start: null, end: null };
    if (preset.key === "custom") return { start: customRange.start || null, end: customRange.end || null };
    return preset.compute();
  }, [rangeKey, customRange]);


  const openEditor = (inv) => {
    setEditing(inv);
    setEditForm({
      supplier: inv.supplier || "",
      invoice_number: inv.invoice_number || "",
      invoice_date: inv.invoice_date || "",
      due_date: inv.due_date || "",
      currency: (inv.currency || "ZAR").toUpperCase(),
      subtotal: inv.subtotal != null ? String(inv.subtotal) : "",
      tax: inv.tax != null ? String(inv.tax) : "",
      total: inv.total != null ? String(inv.total) : "",
      category: inv.category || "",
      status: inv.status || "pending",
      notes: inv.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const payload = {
        supplier: editForm.supplier || null,
        invoice_number: editForm.invoice_number || null,
        invoice_date: editForm.invoice_date || null,
        due_date: editForm.due_date || null,
        currency: editForm.currency || "ZAR",
        subtotal: editForm.subtotal !== "" ? parseFloat(editForm.subtotal) : null,
        tax: editForm.tax !== "" ? parseFloat(editForm.tax) : null,
        total: editForm.total !== "" ? parseFloat(editForm.total) : null,
        category: editForm.category || null,
        status: editForm.status || "pending",
        notes: editForm.notes || null,
      };
      const res = await api.put(`/invoices/${editing.id}`, payload);
      toast.success("Invoice updated");
      // Re-sync the list and the open detail dialog
      setInvoices((prev) => prev.map((i) => (i.id === editing.id ? { ...i, ...res.data } : i)));
      if (active && active.id === editing.id) setActive({ ...active, ...res.data });
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update invoice");
    } finally {
      setSavingEdit(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeRange.start) params.start_date = activeRange.start;
      if (activeRange.end)   params.end_date   = activeRange.end;
      const r = await api.get("/invoices/", { params });
      setInvoices(r.data);
    } finally { setLoading(false); }
  };
  // Reload whenever the active range changes. Persist choice to
  // localStorage so returning users see the same view they left.
  // `load` is intentionally omitted from deps: it's re-created every
  // render, so including it would loop. Range values are the truth.
  useEffect(() => {
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, rangeKey);
      if (rangeKey === "custom") localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(customRange));
    } catch { /* ignore quota */ }
    load();
  }, [activeRange.start, activeRange.end, rangeKey, customRange]); // eslint-disable-line react-hooks/exhaustive-deps
  useQuickEditRefresh(load);

  /** Row click → lazy-fetch the full invoice (with base64 image/PDF blob)
   *  only when the user actually wants to see it. Keeps the list payload
   *  tiny. Shows a spinner in the modal while the detail request runs. */
  const openInvoice = async (inv) => {
    setActive(inv);              // instant: show the row's summary fields
    setActiveLoading(true);
    try {
      const r = await api.get(`/invoices/${inv.id}`);
      setActive((prev) => (prev && prev.id === inv.id ? r.data : prev));
    } catch (e) {
      toast.error("Couldn't load the full invoice");
      console.error(e);
    } finally {
      setActiveLoading(false);
    }
  };

  // Outstanding invoices = anything not yet paid. Kept for the KPI banner
  // subtitle; the PDF now uses `filteredInvoices` (see below) so it reflects
  // whichever view the user has selected.
  const pendingInvoices = useMemo(
    () => invoices.filter((i) => i.status !== "paid"),
    [invoices]
  );

  // Apply the active view filter on top of the date-range results. The
  // TotalCard buttons drive this filter; the table + Budget PDF both render
  // whatever this returns. Free-text search is then layered on top so the
  // KPI cards (which run against the pre-search set) still reflect the
  // full picture for the selected view.
  const filteredInvoices = useMemo(() => {
    let out = invoices;
    if (view === "outstanding") out = out.filter((i) => i.status !== "paid");
    else if (view === "paid") out = out.filter((i) => i.status === "paid");
    else if (view === "thisMonth") {
      const monthStart = new Date();
      monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
      out = out.filter((i) => {
        const d = toDate(i.invoice_date || i.created_at);
        return d !== null && d >= monthStart;
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((i) => {
        const bag = [
          i.supplier,
          i.invoice_number,
          i.category,
          i.notes,
          i.status,
          i.currency,
          i.total != null ? String(i.total) : "",
        ].filter(Boolean).join(" ").toLowerCase();
        return bag.includes(q);
      });
    }
    return out;
  }, [invoices, view, search]);

  const VIEW_LABEL = {
    all: "All in range",
    outstanding: "Outstanding",
    paid: "Paid",
    thisMonth: "This month",
  };

  const exportBudgetPdf = async () => {
    try {
      const source = filteredInvoices;
      const viewLabel = VIEW_LABEL[view] || "All in range";
      const rangeSummary = activeRange.start && activeRange.end
        ? `${activeRange.start} to ${activeRange.end}`
        : activeRange.start
          ? `From ${activeRange.start}`
          : activeRange.end
            ? `Until ${activeRange.end}`
            : "All time";

      const rows = source.map((inv) => ({
        supplier: inv.supplier || "",
        category: inv.category || "",
        invoice_no: inv.invoice_no || "",
        date: safeFormatDate(inv.invoice_date, "yyyy-MM-dd"),
        due: safeFormatDate(inv.due_date, "yyyy-MM-dd"),
        amount:
          inv.total != null
            ? formatCurrency(inv.total, inv.currency)
            : "",
        status: inv.status || "pending",
      }));

      // Totals per currency for the summary chips
      const totalsByCurrency = {};
      for (const inv of source) {
        if (inv.total == null) continue;
        const cur = (inv.currency || "ZAR").toUpperCase();
        totalsByCurrency[cur] = (totalsByCurrency[cur] || 0) + Number(inv.total);
      }
      // 3-cell summary band: item count, total(s), and range.
      const summary = [
        { label: "Invoices", value: source.length },
        ...Object.entries(totalsByCurrency).map(([cur, val]) => ({
          label: cur === "ZAR" ? "Total" : `Total ${cur}`,
          value: formatCurrency(val, cur),
          mono: true,
        })),
        { label: "Period", value: rangeSummary },
      ];

      // Filename: budget-<view>-<range>.pdf so the file itself makes the
      // filter obvious even after download.
      const rangeToken = activeRange.start && activeRange.end
        ? `${activeRange.start}_${activeRange.end}`
        : "all-time";
      const filename = `budget-${view}-${rangeToken}`;

      const { downloadReport } = await import("@/lib/pdfReport");
      await downloadReport({
        eyebrow: "BUDGET",
        title: viewLabel,
        period: rangeSummary,
        filename,
        summary,
        columns: [
          { header: "Supplier", dataKey: "supplier" },
          { header: "Category", dataKey: "category" },
          { header: "Invoice #", dataKey: "invoice_no", mono: true },
          { header: "Invoice date", dataKey: "date", mono: true },
          { header: "Due", dataKey: "due", mono: true },
          { header: "Amount", dataKey: "amount", mono: true, halign: "right" },
          { header: "Status", dataKey: "status", status: true },
        ],
        rows,
      });
    } catch (e) {
      toast.error("Could not generate budget PDF");
      console.error(e);
    }
  };

  const totals = useMemo(() => {
    const sum = (rows) => {
      const out = {};
      for (const inv of rows) {
        if (inv.total == null) continue;
        const cur = (inv.currency || "ZAR").toUpperCase();
        out[cur] = (out[cur] || 0) + Number(inv.total);
      }
      return out;
    };
    const isPaid = (i) => i.status === "paid";
    const unpaid = invoices.filter((i) => !isPaid(i));
    const paid = invoices.filter(isPaid);
    const monthStart = new Date();
    monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    const thisMonth = invoices.filter((i) => {
      const d = toDate(i.invoice_date || i.created_at);
      return d !== null && d >= monthStart;
    });
    return {
      unpaid: sum(unpaid), unpaidCount: unpaid.length,
      paid: sum(paid), paidCount: paid.length,
      thisMonth: sum(thisMonth), thisMonthCount: thisMonth.length,
      total: sum(invoices), totalCount: invoices.length,
    };
    // All the "missing deps" the linter flags (sum, isPaid, unpaid, paid,
    // cur, d, i, monthStart, thisMonth) are locally scoped inside this
    // useMemo callback and re-created on every run — they're NOT from the
    // enclosing scope. `invoices` is the only real dependency. Standard
    // false positive from react-hooks/exhaustive-deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  const upload = async (file) => {
    if (!file) return;
    const ok = file.type?.startsWith("image/") || file.type === "application/pdf"
      || /\.(png|jpe?g|webp|pdf)$/i.test(file.name || "");
    if (!ok) { toast.error("Please upload a PDF or an image (PNG/JPEG/WEBP)"); return; }
    if (file.size > 16 * 1024 * 1024) { toast.error("File too large (max 16MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.is_pdf ? "PDF" : "Image"} extracted: ${res.data.supplier || "invoice"}. ${res.data.currency} ${res.data.total ?? "?"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const verify = async (id) => {
    try {
      const r = await api.post(`/invoices/${id}/verify`);
      toast.success("Invoice verified");
      load();
      setActive(r.data);
    } catch (e) { toast.error("Failed"); }
  };

  const markPaid = async (id, e) => {
    if (e) e.stopPropagation();
    setActing(id);
    try {
      const r = await api.post(`/invoices/${id}/mark-paid`);
      toast.success("Marked as paid");
      load();
      if (active?.id === id) setActive(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark as paid");
    } finally { setActing(null); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        subtitle={
          isAdmin
            ? "AI-extracted from uploads + conversational entry via WhatsApp / Quick edit"
            : "Read-only view. Admins can upload, verify, and mark invoices paid."
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportBudgetPdf}
              disabled={loading || filteredInvoices.length === 0}
              data-testid="invoices-download-budget-pdf-button"
            >
              <FileDown className="h-4 w-4 mr-2" /> Budget PDF{view !== "all" ? ` (${VIEW_LABEL[view]})` : ""}
            </Button>
            {isAdmin ? (
              <label className="inline-flex">
                <input type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="invoice-upload-input" />
                <span className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium cursor-pointer hover:bg-primary/92">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Upload invoice (PDF or image)</>}
                </span>
              </label>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground" data-testid="invoices-readonly-badge">
                <Eye className="h-3.5 w-3.5" /> Read-only
              </span>
            )}
          </div>
        }
      />

      {/* Date range picker — chip presets + custom start/end */}
      <div className="rounded-lg border bg-card/50 px-4 py-3 space-y-2" data-testid="invoices-range-picker">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarIcon className="h-4 w-4 text-[hsl(var(--ap-forest))]" />
            <span className="font-medium text-foreground">Date range</span>
            {activeRange.start || activeRange.end ? (
              <span className="text-xs" data-testid="invoices-range-summary">
                · {activeRange.start || "…"} → {activeRange.end || "…"}
              </span>
            ) : (
              <span className="text-xs">· All time</span>
            )}
          </div>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" data-testid="invoices-range-loading" />}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setRangeKey(p.key)}
              data-testid={`invoices-range-${p.key}`}
              className={
                rangeKey === p.key
                  ? "inline-flex items-center rounded-full bg-[hsl(var(--ap-forest))] text-white px-3 py-1 text-xs font-medium ring-1 ring-[hsl(var(--ap-forest))]"
                  : "inline-flex items-center rounded-full bg-transparent text-foreground/70 hover:text-foreground hover:bg-accent px-3 py-1 text-xs font-medium ring-1 ring-border"
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        {rangeKey === "custom" && (
          <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="invoices-range-custom-inputs">
            <Label htmlFor="range-start" className="text-xs text-muted-foreground shrink-0">From</Label>
            <Input
              id="range-start"
              type="date"
              value={customRange.start || ""}
              max={customRange.end || undefined}
              onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value }))}
              className="h-8 w-[160px] text-xs"
              data-testid="invoices-range-custom-start"
            />
            <Label htmlFor="range-end" className="text-xs text-muted-foreground shrink-0">to</Label>
            <Input
              id="range-end"
              type="date"
              value={customRange.end || ""}
              min={customRange.start || undefined}
              onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value }))}
              className="h-8 w-[160px] text-xs"
              data-testid="invoices-range-custom-end"
            />
          </div>
        )}
      </div>

      {/* Search — free-text filter across supplier / invoice number /
          category / notes / status. Applied on top of the date range and
          KPI-card view filter, so the KPI totals reflect the full range
          while the table + PDF reflect exactly what the user is looking
          at. */}
      <div className="relative" data-testid="invoices-search-wrapper">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          placeholder="Search by supplier, invoice number, category, notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-9"
          data-testid="invoices-search-input"
          aria-label="Search invoices"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
            data-testid="invoices-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Summary totals. computed against the currently-filtered set.
          Each card is a click-target that applies a view filter to the table. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="invoices-totals">
        <TotalCard label="Outstanding" icon={Clock}        totals={totals.unpaid}   count={totals.unpaidCount}   tone="alert" testId="invoices-total-unpaid" selected={view === "outstanding"} onClick={() => setViewAndUrl(view === "outstanding" ? "all" : "outstanding")} />
        <TotalCard label="Paid"        icon={CheckCircle2} totals={totals.paid}     count={totals.paidCount}     tone="good"  testId="invoices-total-paid"   selected={view === "paid"}        onClick={() => setViewAndUrl(view === "paid" ? "all" : "paid")} />
        <TotalCard label="In range"    icon={Banknote}     totals={totals.total}    count={totals.totalCount}                  testId="invoices-total-range"  selected={view === "all"}         onClick={() => setViewAndUrl("all")} />
        <TotalCard label={rangeKey === "all" ? "All time" : "This month"} icon={CircleDollarSign} totals={totals.thisMonth} count={totals.thisMonthCount} testId="invoices-total-month" selected={view === "thisMonth"} onClick={() => setViewAndUrl(view === "thisMonth" ? "all" : "thisMonth")} />
      </div>

      {/* Filter breadcrumb. tells the user exactly what the table + PDF will contain */}
      {(view !== "all" || search.trim()) && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap" data-testid="invoices-filter-banner">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--ap-forest)/0.10)] text-[hsl(var(--ap-forest))] border border-[hsl(var(--ap-forest)/0.25)] px-2.5 py-1 font-medium">
            Showing: {VIEW_LABEL[view]}{search.trim() ? ` · matching "${search.trim()}"` : ""} · {filteredInvoices.length} of {invoices.length}
          </span>
          {view !== "all" && (
            <button type="button" onClick={() => setViewAndUrl("all")} className="underline underline-offset-2 hover:text-foreground" data-testid="invoices-filter-clear">Clear view filter</button>
          )}
          {search.trim() && (
            <button type="button" onClick={() => setSearch("")} className="underline underline-offset-2 hover:text-foreground" data-testid="invoices-search-clear-inline">Clear search</button>
          )}
        </div>
      )}

      {/* Conversational entry hint */}
      <div className="rounded-lg border border-[hsl(var(--ap-forest)/0.2)] bg-[hsl(var(--ap-forest)/0.04)] px-4 py-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="invoices-conversational-hint">
        <Sparkles className="h-4 w-4 mt-0.5 text-[hsl(var(--ap-forest))] shrink-0" />
        <span>
          <strong className="text-foreground">Tip:</strong> log invoices informally by typing into WhatsApp or Quick edit,
          e.g. <em>&ldquo;Got an invoice from Roberson Hay for R3,500 due in 30 days&rdquo;</em>. The system extracts the supplier, amount, currency, category and due date automatically.
        </span>
      </div>

      <Card className="rounded-xl overflow-hidden">
        {/* Desktop / tablet: table view */}
        <div className="hidden md:block overflow-auto" data-testid="invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ap-paper))]"><tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Supplier</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
                : filteredInvoices.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">{invoices.length === 0 ? "No invoices yet. Upload one above, send a photo via WhatsApp, or type an informal description in Quick edit." : search.trim() ? `No invoices match your search "${search.trim()}".` : `No invoices match the "${VIEW_LABEL[view]}" filter in this date range.`}</td></tr>
                : filteredInvoices.map((inv) => {
                  const paid = inv.status === "paid";
                  return (
                    <tr key={inv.id} onClick={() => openInvoice(inv)} className={`border-b hover:bg-accent/60 cursor-pointer ${paid ? "opacity-70" : ""}`} data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 text-muted-foreground">{safeFormatDateOnly(inv.invoice_date, "MMM d, yyyy", safeFormatDate(inv.created_at, "MMM d, yyyy"))}</td>
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          {inv.is_pdf && <span className="inline-flex items-center gap-1 rounded border border-[hsl(var(--ap-info)/0.3)] bg-[hsl(var(--ap-info)/0.08)] text-[hsl(var(--ap-info))] px-1.5 py-0.5 text-[10px] font-medium" data-testid="invoice-pdf-badge"><FileType className="h-2.5 w-2.5" />PDF</span>}
                          {inv.source === "quick_edit" && <span className="inline-flex items-center gap-1 rounded border border-[hsl(var(--ap-forest)/0.3)] bg-[hsl(var(--ap-forest)/0.08)] text-[hsl(var(--ap-forest))] px-1.5 py-0.5 text-[10px] font-medium"><Sparkles className="h-2.5 w-2.5" />AI</span>}
                          <span>{inv.supplier || ""}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground capitalize">{inv.category || ""}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{inv.total != null ? formatCurrency(inv.total, inv.currency) : ""}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {safeFormatDateOnly(inv.due_date, "MMM d")}
                      </td>
                      <td className="px-4 py-3"><StatusPill status={inv.status} /></td>
                      <td className="px-4 py-3 text-right">
                        {!paid && isAdmin && (
                          <Button
                            size="sm"
                            disabled={acting === inv.id}
                            onClick={(e) => markPaid(inv.id, e)}
                            data-testid={`invoice-mark-paid-${inv.id}`}
                            className="h-7 px-3 text-xs bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90"
                          >
                            {acting === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Mark paid"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Mobile: tap-friendly card list */}
        <div className="md:hidden divide-y" data-testid="invoices-cards">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              {invoices.length === 0
                ? "No invoices yet. Upload one above, send a photo via WhatsApp, or type an informal description in Quick edit."
                : `No invoices match the "${VIEW_LABEL[view]}" filter in this date range.`}
            </div>
          ) : filteredInvoices.map((inv) => {
            const paid = inv.status === "paid";
            return (
              <button
                key={inv.id}
                type="button"
                onClick={() => openInvoice(inv)}
                data-testid={`invoice-card-${inv.id}`}
                className={`w-full text-left px-4 py-4 hover:bg-accent/60 active:bg-accent/80 transition-colors ${paid ? "opacity-70" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {inv.is_pdf && <span className="inline-flex items-center gap-1 rounded border border-[hsl(var(--ap-info)/0.3)] bg-[hsl(var(--ap-info)/0.08)] text-[hsl(var(--ap-info))] px-1.5 py-0.5 text-[10px] font-medium"><FileType className="h-2.5 w-2.5" />PDF</span>}
                      {inv.source === "quick_edit" && <span className="inline-flex items-center gap-1 rounded border border-[hsl(var(--ap-forest)/0.3)] bg-[hsl(var(--ap-forest)/0.08)] text-[hsl(var(--ap-forest))] px-1.5 py-0.5 text-[10px] font-medium"><Sparkles className="h-2.5 w-2.5" />AI</span>}
                      <StatusPill status={inv.status} />
                    </div>
                    <div className="font-medium mt-1.5 truncate" data-testid={`invoice-card-supplier-${inv.id}`}>
                      {inv.supplier || ""}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      {inv.category && <span className="capitalize">{inv.category}</span>}
                      {inv.category && <span>·</span>}
                      <span>{safeFormatDateOnly(inv.invoice_date, "MMM d, yyyy", safeFormatDate(inv.created_at, "MMM d, yyyy"))}</span>
                      {inv.due_date && (
                        <>
                          <span>·</span>
                          <span>Due {safeFormatDateOnly(inv.due_date, "MMM d")}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-base tabular-nums font-medium">
                      {inv.total != null ? formatCurrency(inv.total, inv.currency) : ""}
                    </div>
                    {!paid && isAdmin && (
                      <Button
                        size="sm"
                        disabled={acting === inv.id}
                        onClick={(e) => markPaid(inv.id, e)}
                        data-testid={`invoice-mark-paid-mobile-${inv.id}`}
                        className="mt-2 h-8 px-3 text-xs bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90"
                      >
                        {acting === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Mark paid"}
                      </Button>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">Invoice details {active?.is_pdf && <span className="inline-flex items-center gap-1 rounded border border-[hsl(var(--ap-info)/0.3)] bg-[hsl(var(--ap-info)/0.08)] text-[hsl(var(--ap-info))] px-2 py-0.5 text-[11px] font-medium"><FileType className="h-3 w-3" />PDF</span>}</DialogTitle></DialogHeader>
          {active && (
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="max-h-[600px] overflow-auto rounded-md border bg-muted/30" data-testid="invoice-photo-viewer">
                  {activeLoading && !active.image_base64 && !active.pdf_base64 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
                      <Loader2 className="h-6 w-6 animate-spin opacity-60" />
                      Loading scan…
                    </div>
                  ) : active.is_pdf && active.pdf_base64 ? (
                    <iframe title="Invoice PDF" src={`data:application/pdf;base64,${active.pdf_base64}`} className="w-full" style={{ height: 600, border: 0 }} data-testid="invoice-pdf-iframe" />
                  ) : active.image_base64 ? (
                    <img src={`data:${active.image_mime || "image/jpeg"};base64,${active.image_base64}`} alt="invoice" className="w-full h-auto" />
                  ) : (
                    <div className="p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
                      <ReceiptText className="h-8 w-8 opacity-40" />
                      {active.source === "quick_edit" ? "Logged via Quick edit (no file attached)" : "No file"}
                    </div>
                  )}
                </div>
                {active.is_pdf && active.pdf_base64 && (
                  <a
                    href={`data:application/pdf;base64,${active.pdf_base64}`}
                    download={active.filename || `invoice-${active.id}.pdf`}
                    className="inline-flex items-center gap-2 text-xs text-[hsl(var(--ap-info))] hover:underline"
                    data-testid="invoice-download-pdf"
                  >
                    <Download className="h-3.5 w-3.5" /> Download original PDF{active.filename ? ` (${active.filename})` : ""}
                  </a>
                )}
              </div>
              <div className="space-y-2 text-sm" data-testid="invoice-extracted-fields">
                <Field label="Supplier" value={active.supplier} />
                <Field label="Invoice #" value={active.invoice_number} mono />
                <Field label="Date" value={active.invoice_date} />
                <Field label="Due date" value={active.due_date} />
                <Field label="Currency" value={active.currency} />
                <Field label="Subtotal" value={active.subtotal != null ? formatCurrency(active.subtotal, active.currency) : null} mono />
                <Field label="Tax" value={active.tax != null ? formatCurrency(active.tax, active.currency) : null} mono />
                <Field label="Total" value={active.total != null ? formatCurrency(active.total, active.currency) : null} mono big />
                <Field label="Category" value={active.category} />
                <Field label="Status"><StatusPill status={active.status} /></Field>
                <Field label="Source" value={active.source} />
                {active.notes && <Field label="Notes" value={active.notes} />}
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-3 mb-2">Line items</div>
                  {(active.line_items || []).length === 0 ? <div className="text-xs text-muted-foreground">none</div>
                    : <div className="space-y-1">{active.line_items.map((li, i) => (
                      <div key={`li-${li.description || "row"}-${li.quantity ?? "-"}-${li.line_total ?? i}`} className="text-xs flex justify-between gap-2 py-1 border-b last:border-0">
                        <span className="flex-1">{li.description}</span>
                        <span className="font-mono">{li.quantity} × {li.unit_price ?? ""}</span>
                        <span className="font-mono font-medium">{li.line_total ?? ""}</span>
                      </div>
                    ))}</div>}
                </div>
                <div className="grid grid-cols-2 gap-2 pt-3 mt-3 border-t">
                  {/* Master admin can edit every field on the invoice. useful when
                      the AI extraction missed the supplier or got the amount wrong. */}
                  {isMaster && (
                    <Button
                      variant="outline"
                      onClick={() => openEditor(active)}
                      data-testid="invoice-edit-button"
                      className="col-span-2"
                    >
                      <Pencil className="h-4 w-4 mr-2" /> Edit invoice details
                    </Button>
                  )}
                  {isAdmin && active.status !== "verified" && active.status !== "paid" && (
                    <Button variant="outline" onClick={() => verify(active.id)} data-testid="invoice-verify-button">
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Verify
                    </Button>
                  )}
                  {isAdmin && active.status !== "paid" && (
                    <Button
                      onClick={() => markPaid(active.id)}
                      disabled={acting === active.id}
                      data-testid="invoice-detail-mark-paid"
                      className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90"
                    >
                      {acting === active.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Mark paid
                    </Button>
                  )}
                  {!isAdmin && (
                    <div className="col-span-2 text-center text-xs text-muted-foreground">
                      Only admins can verify or mark invoices as paid.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Master-admin invoice editor. fixes missed supplier / wrong amounts */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-auto" data-testid="invoice-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit invoice</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Correct any field the AI missed. supplier name, total, currency, dates, category, notes.
              All changes are logged in the audit trail.
            </p>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="col-span-2">
                <Label>Supplier *</Label>
                <Input
                  value={editForm.supplier}
                  onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })}
                  placeholder="e.g. Roberson Hay"
                  data-testid="invoice-edit-supplier"
                />
              </div>
              <div>
                <Label>Invoice #</Label>
                <Input
                  value={editForm.invoice_number}
                  onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                  data-testid="invoice-edit-number"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  placeholder="feed, tack, vet, fuel, …"
                  data-testid="invoice-edit-category"
                />
              </div>
              <div>
                <Label>Invoice date</Label>
                <Input
                  type="date"
                  value={editForm.invoice_date}
                  onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })}
                  data-testid="invoice-edit-date"
                />
              </div>
              <div>
                <Label>Due date</Label>
                <Input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  data-testid="invoice-edit-due"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={editForm.currency}
                  onValueChange={(v) => setEditForm({ ...editForm, currency: v })}
                >
                  <SelectTrigger data-testid="invoice-edit-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ZAR">ZAR (R)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger data-testid="invoice-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">pending</SelectItem>
                    <SelectItem value="verified">verified</SelectItem>
                    <SelectItem value="paid">paid</SelectItem>
                    <SelectItem value="disputed">disputed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subtotal</Label>
                <Input
                  type="number" step="0.01"
                  value={editForm.subtotal}
                  onChange={(e) => setEditForm({ ...editForm, subtotal: e.target.value })}
                  data-testid="invoice-edit-subtotal"
                />
              </div>
              <div>
                <Label>Tax</Label>
                <Input
                  type="number" step="0.01"
                  value={editForm.tax}
                  onChange={(e) => setEditForm({ ...editForm, tax: e.target.value })}
                  data-testid="invoice-edit-tax"
                />
              </div>
              <div className="col-span-2">
                <Label>Total *</Label>
                <Input
                  type="number" step="0.01"
                  value={editForm.total}
                  onChange={(e) => setEditForm({ ...editForm, total: e.target.value })}
                  data-testid="invoice-edit-total"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Anything to flag for the next reader"
                  data-testid="invoice-edit-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="invoice-edit-save">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TotalCard({ label, icon: Icon, totals, count, tone, testId, selected, onClick }) {
  const entries = Object.entries(totals);
  const toneClass =
    tone === "alert" ? "border-amber-300/50 bg-amber-50/30"
    : tone === "good" ? "border-emerald-300/50 bg-emerald-50/30"
    : "";
  const interactive = typeof onClick === "function";
  const selectedRing = selected ? "ring-2 ring-[hsl(var(--ap-forest))] ring-offset-1" : "";
  const interactiveCls = interactive
    ? "cursor-pointer transition-transform transition-shadow hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    : "";
  const cardEl = (
    <Card
      className={`rounded-xl ${toneClass} ${selectedRing} ${interactiveCls} text-left w-full`}
      data-testid={testId}
      data-selected={selected ? "true" : "false"}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        {entries.length === 0 ? (
          <div className="font-serif text-3xl mt-2 tabular-nums" data-testid={`${testId}-value`}></div>
        ) : entries.length === 1 ? (
          <div className="font-serif text-3xl mt-2 tabular-nums" data-testid={`${testId}-value`}>
            {formatCurrency(entries[0][1], entries[0][0])}
          </div>
        ) : (
          <div className="mt-2 space-y-0.5" data-testid={`${testId}-value`}>
            {entries.map(([cur, amt]) => (
              <div key={cur} className="font-serif text-lg tabular-nums leading-tight">
                {formatCurrency(amt, cur)}
              </div>
            ))}
          </div>
        )}
        <div className="text-xs text-muted-foreground mt-1">{count} invoice{count === 1 ? "" : "s"}{selected ? " · showing" : ""}</div>
      </CardContent>
    </Card>
  );
  if (!interactive) return cardEl;
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left rounded-xl focus-visible:outline-none"
      data-testid={`${testId}-button`}
      aria-pressed={selected ? "true" : "false"}
      aria-label={`${label}. ${selected ? "clear filter" : "apply filter"}`}
    >
      {cardEl}
    </button>
  );
}

function Field({ label, value, mono, big, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b last:border-0">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`${mono ? "font-mono tabular-nums" : ""} ${big ? "text-lg font-semibold" : ""} text-foreground`}>{children ?? value ?? ""}</span>
    </div>
  );
}
